/**
 * The view settings pane (`specs/M4-V1-views.md` §8).
 *
 * A 320 px right-hand pane holding the view's name, its description and whatever its **registry
 * entry** declares — nothing here knows what a kanban's settings look like, which is the point
 * of the registry (`README-dev.md` · "Registries, not special cases").
 *
 * A **locked** view renders read-only with monday's own sentence, and every control is
 * `disabled` rather than hidden, so a user can see what they are not allowed to change.
 */
import { useOverlayDismiss } from '@elizmonday/ui';
import { Button, TextField, viewTypeRegistry } from './registry';
import { useState } from 'react';
import './views.css';

export interface ViewSettingsTarget {
  viewId: string | null;
  name: string;
  registryId: string | null;
  locked: boolean;
  pinned: boolean;
  hidden: boolean;
  visibility: string;
  isDefault: boolean;
  settings: Record<string, unknown>;
}

export function ViewSettingsPane({
  target,
  canEdit,
  onRename,
  onClose,
}: {
  target: ViewSettingsTarget;
  canEdit: boolean;
  onRename(name: string): void;
  onClose(): void;
}) {
  const [name, setName] = useState(target.name);
  /**
   * **P-260.** A pane is an overlay like any other: Escape closes it and focus goes back to the
   * `View settings` row's own opener — the tab's `···`, since the tab menu unmounted in the
   * commit that mounted this (the hoisted pattern). On a phone the pane is a bottom sheet
   * (**P-261**), where that is the only keyboard way out of it.
   */
  useOverlayDismiss({ onClose });
  const definition = target.registryId === null ? undefined : viewTypeRegistry.find(target.registryId);
  const readOnly = target.locked && !canEdit;

  return (
    <aside
      className="ez-view-settings"
      aria-label={`${target.name} settings`}
      data-testid="view-settings-pane"
      data-readonly={readOnly || undefined}
    >
      <header className="ez-view-settings__head">
        <h2>View settings</h2>
        <button type="button" aria-label="Close view settings" onClick={onClose} data-testid="view-settings-close">
          ×
        </button>
      </header>

      {readOnly ? (
        <p className="ez-view-settings__locked" data-testid="view-settings-locked">
          Only board owners will be able to edit this view.
        </p>
      ) : null}

      <label className="ez-view-settings__field">
        <span>Name</span>
        <TextField
          value={name}
          disabled={readOnly}
          data-testid="view-settings-name"
          onChange={(next: string) => setName(next)}
          onBlur={() => {
            if (!readOnly && name.trim() !== '' && name !== target.name) onRename(name.trim());
          }}
        />
      </label>

      <dl className="ez-view-settings__facts">
        <div>
          <dt>Type</dt>
          <dd data-testid="view-settings-type">{definition?.title ?? target.registryId ?? 'Unknown'}</dd>
        </div>
        <div>
          <dt>Visibility</dt>
          <dd data-testid="view-settings-visibility">
            {target.visibility === 'personal' ? 'Personal' : 'Shared'}
          </dd>
        </div>
        <div>
          <dt>Default view</dt>
          <dd>{target.isDefault ? 'Yes' : 'No'}</dd>
        </div>
        <div>
          <dt>Locked</dt>
          <dd data-testid="view-settings-locked-state">{target.locked ? 'Yes' : 'No'}</dd>
        </div>
      </dl>

      {definition !== undefined && !definition.implemented ? (
        <p className="ez-view-settings__hint" data-testid="view-settings-hint">
          {definition.title} views land with {definition.plannedIn}. Their configuration is stored
          and returned untouched until then.
        </p>
      ) : null}

      <footer className="ez-view-settings__foot">
        <Button kind="tertiary" onClick={onClose} data-testid="view-settings-done">
          Done
        </Button>
      </footer>
    </aside>
  );
}
